/// <amd-module name="VSS/Features/Experiments/ExperimentService" />
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { isFeatureEnabled } from "VSS/Platform/Feature";

/**
 * Service for working with experiments
 */
export interface IExperimentService extends IVssService {
    /**
     * Call this whenever the user is exposed to the experiment.
     *
     * @param experimentId The id of the experiment
     */
    publishLoaded(experimentId: string): void;

    /**
     * Use this to analyze the change in average of a value.
     *
     * @param experimentId The id of the experiment
     * @param measurementName The name of this measurement (experiments may have multiple measurements)
     * @param value The current value of this measurement
     */
    publishAverageMeasurement(experimentId: string, measurementName: string, value: number): void;

    /**
     * Use this to analyze the change in click thru rate (number of clicks / number of page loads).
     *
     * @param experimentId The id of the experiment
     * @param measurementName The name of this measurement (experiments may have multiple measurements)
     */
    publishClickThruRateMeasurement(experimentId: string, measurementName: string): void;

    /**
     *  Use this to analyze the number of times a user performs an action
     *
     * @param experimentId The id of the experiment
     * @param measurementName The name of this measurement (experiments may have multiple measurements)
     */
    publishCountMeasurement(experimentId: string, measurementName: string): void;
}

class ExperimentService extends VssService implements IVssService {
    publishLoaded(experimentId: string) {
        let properties: { [key: string]: any } = {};
        this.addFrameworkProperties(experimentId, "load", properties);
        this.publishTelemetry(experimentId, properties);
    }

    publishAverageMeasurement(experimentId: string, measurementName: string, value: number) {
        this.publishMeasurement(experimentId, "average", measurementName, { value: value });
    }

    publishClickThruRateMeasurement(experimentId: string, measurementName: string) {
        this.publishMeasurement(experimentId, "clickthru", measurementName, {});
    }

    publishCountMeasurement(experimentId: string, measurementName: string) {
        this.publishMeasurement(experimentId, "count", measurementName, {});
    }

    private publishTelemetry(experimentId: string, properties: { [key: string]: any }) {
        const telemetryService = this.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent("ab-tests", experimentId, properties);
    }

    private publishMeasurement(experimentId: string, measurementType: string, measurementName: string, properties: { [key: string]: any }) {
        this.addFrameworkProperties(experimentId, measurementType, properties);
        this.addFrameworkProperty(properties, "MeasurementName", measurementName);
        this.publishTelemetry(experimentId, properties);
    }

    private addFrameworkProperty(properties: { [key: string]: any }, key: string, value: any) {
        if (properties[key]) {
            throw new Error(`${key} is reserved, and supplied automatically.`);
        }
        properties[key] = value;
    }

    private addFrameworkProperties(experimentId: string, measurementType: string, properties: { [key: string]: any }) {
        let experimentVariant = isFeatureEnabled(this.pageContext, experimentId) ? "Variation" : "Control";
        this.addFrameworkProperty(properties, "MeasurementType", measurementType);
        this.addFrameworkProperty(properties, "ExperimentVariant", experimentVariant);
        this.addFrameworkProperty(properties, "ExperimentSchemaVersion", 1);
    }
}

Services.add("IExperimentService", { serviceFactory: ExperimentService });
